export function calculateBundleTotals(caseOneCents, caseTwoCents, accessoryCents) {
  const values = [caseOneCents, caseTwoCents, accessoryCents];
  if (values.some((value) => !Number.isInteger(value) || value < 0)) {
    throw new TypeError('Bundle prices must be non-negative integer cents');
  }

  const subtotalCents = caseOneCents + caseTwoCents + accessoryCents;
  const discountCents = Math.round(accessoryCents * 0.5);
  const totalCents = subtotalCents - discountCents;

  return {
    subtotalCents,
    discountCents,
    totalCents,
    freeShippingUnlocked: totalCents >= 6000,
  };
}

export function isBundleEligible(selection) {
  return Boolean(
    selection?.firstVariantId &&
    selection?.secondVariantId &&
    selection?.accessoryVariantId
  );
}

export function formatMoney(cents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

export function isEligibleBundleProduct(product) {
  const searchableText = [product?.title, product?.handle, product?.body_html]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const digitalOnlySignals = [
    'digital download',
    'instant download',
    'downloadable digital',
    'no physical goods',
    'no physical product',
  ];
  return !digitalOnlySignals.some((signal) => searchableText.includes(signal));
}

export function normalizeCollectionProduct(product) {
  return {
    id: Number(product.id),
    title: product.title,
    handle: product.handle,
    image: product.images?.[0]?.src || '',
    optionNames: (product.options || []).map((option) => option.name),
    variants: (product.variants || []).map((variant) => ({
      id: Number(variant.id),
      title: variant.title,
      options: [variant.option1, variant.option2, variant.option3].filter(Boolean),
      available: Boolean(variant.available),
      priceCents: Math.round(Number.parseFloat(variant.price) * 100),
    })),
  };
}

export async function loadProducts(url) {
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Unable to load products (${response.status})`);
  const payload = await response.json();
  return (payload.products || [])
    .filter(isEligibleBundleProduct)
    .map(normalizeCollectionProduct);
}

export function rankInitialProducts(products, preferredTerms, limit) {
  const terms = preferredTerms.map((term) => term.toLowerCase());
  return products
    .map((product, index) => {
      const title = product.title.toLowerCase();
      const preferredIndex = terms.findIndex((term) => title.includes(term));
      return {
        product,
        index,
        rank: preferredIndex === -1 ? terms.length : preferredIndex,
      };
    })
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .slice(0, limit)
    .map(({ product }) => product);
}

export function findVariant(product, selectedOptions) {
  if (selectedOptions.length === 0 && !shouldRenderProductOptions(product)) {
    return product.variants.find((variant) => variant.available) || null;
  }
  return product.variants.find((variant) =>
    variant.available &&
    variant.options.length === selectedOptions.length &&
    variant.options.every((value, index) => value === selectedOptions[index])
  ) || null;
}

export function shouldRenderProductOptions(product) {
  return product.variants.length > 1;
}

export function buildCartLines(selection) {
  if (!isBundleEligible(selection)) {
    throw new Error('Select two phone-case variants and one accessory variant');
  }

  return [
    { id: selection.firstVariantId, quantity: 1, properties: { _klover_bundle: 'free-shipping-set', _klover_bundle_role: 'primary_case' } },
    { id: selection.secondVariantId, quantity: 1, properties: { _klover_bundle: 'free-shipping-set', _klover_bundle_role: 'second_case' } },
    { id: selection.accessoryVariantId, quantity: 1, properties: { _klover_bundle: 'free-shipping-set', _klover_bundle_role: 'accessory' } },
  ];
}

function cartRoot() {
  const root = window.Shopify?.routes?.root || window.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

export async function fetchCart() {
  const response = await fetch(`${cartRoot()}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Unable to read the current cart');
  return response.json();
}

export async function addBundleToCart(selection) {
  const lines = buildCartLines(selection);
  const response = await fetch(`${cartRoot()}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ items: lines }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const bundleError = new Error(error.description || 'Unable to add the complete set');
    bundleError.status = response.status;
    throw bundleError;
  }

  return fetchCart();
}

export function verifyBundleCartLines(cart, selection) {
  const expectedIds = [
    selection?.firstVariantId,
    selection?.secondVariantId,
    selection?.accessoryVariantId,
  ].map(Number);
  if (expectedIds.some((id) => !Number.isSafeInteger(id) || id <= 0)) return false;

  const bundleIds = new Set(
    (cart?.items || [])
      .filter((item) => item.properties?._klover_bundle === 'free-shipping-set')
      .map((item) => Number(item.variant_id || item.id))
  );
  return expectedIds.every((id) => bundleIds.has(id));
}

export function confirmBundleDiscount(cart) {
  const accessoryLines = (cart?.items || []).filter((item) =>
    item.properties?._klover_bundle === 'free-shipping-set' &&
    item.properties?._klover_bundle_role === 'accessory'
  );
  if (accessoryLines.length !== 1) return false;

  const accessory = accessoryLines[0];
  const original = Number(accessory.original_line_price);
  const final = Number(accessory.final_line_price);
  const allocated = (accessory.discount_allocations || [])
    .reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0);

  return Number.isInteger(original) &&
    original > 0 &&
    final * 2 === original &&
    allocated * 2 === original;
}

export function deriveCartNudgeState(cart, context = {}) {
  if (confirmBundleDiscount(cart) && Number(cart?.total_price) >= 6000) {
    return 'unlocked';
  }

  const qualifyingHandles = context.qualifyingCaseHandles || new Set();
  let qualifyingCaseQuantity = 0;
  let hasBundleAccessory = false;

  for (const item of cart?.items || []) {
    const quantity = Math.max(0, Number(item.quantity) || 0);
    const role = item.properties?._klover_bundle_role;
    const isQualifyingCase =
      role === 'primary_case' ||
      role === 'second_case' ||
      item.handle === context.pilotHandle ||
      qualifyingHandles.has(item.handle);
    if (isQualifyingCase) qualifyingCaseQuantity += quantity;
    if (role === 'accessory') hasBundleAccessory = true;
  }

  if (qualifyingCaseQuantity === 1) return 'single_case';
  if (qualifyingCaseQuantity >= 2 && !hasBundleAccessory) return 'missing_accessory';
  return 'none';
}

const CASE_PREFERRED_TITLES = [
  'Summer Citrus Fruit Cream Base Shockproof Anti-slip iPhone Case',
  'Cute Scattered Red Cherry Leaf Shockproof Anti-slip iPhone Case',
  'Vintage Blue Line Marine Fruit Palm Beige Base Coastal iPhone Cover',
];

const ACCESSORY_PREFERRED_TERMS = [
  'yellow flower',
  'yellow suction',
  'light blue suction',
  'pink',
];

const SELECTION_EVENT = 'klover:bundle-selection';
const PILOT_HANDLE = 'tropical-palm-tree-iphone-case-with-sunflower-art-summer-protective';
const CART_COLLECTION_URLS = {
  iphone: '/collections/apple-series-phone-cases/products.json?limit=250',
  accessories: '/collections/phone-accessories/products.json?limit=250',
};
const cartNudgeContext = {
  pilotHandle: PILOT_HANDLE,
  qualifyingCaseHandles: new Set([PILOT_HANDLE]),
  accessories: [],
  currency: 'USD',
};

function uniqueProducts(products) {
  return [...new Map(products.map((product) => [product.id, product])).values()];
}

export async function loadBundleCatalog({ iphoneUrl, accessoriesUrl }) {
  const [iphoneProducts, accessoryProducts] = await Promise.all([
    loadProducts(iphoneUrl),
    loadProducts(accessoriesUrl),
  ]);
  const eligibleCases = uniqueProducts(iphoneProducts).filter((product) => {
    const minimumPrice = lowestAvailablePrice([product]);
    return minimumPrice !== null && minimumPrice >= 2990;
  });
  return {
    cases: rankInitialProducts(eligibleCases, CASE_PREFERRED_TITLES, 6),
    accessories: uniqueProducts(accessoryProducts),
  };
}

async function hydrateCartNudgeContext() {
  if (cartNudgeContext.hydrated) return;
  const catalog = await loadBundleCatalog({
    iphoneUrl: CART_COLLECTION_URLS.iphone,
    accessoriesUrl: CART_COLLECTION_URLS.accessories,
  });
  cartNudgeContext.qualifyingCaseHandles = new Set([
    PILOT_HANDLE,
    ...catalog.cases.map((product) => product.handle),
  ]);
  cartNudgeContext.accessories = catalog.accessories;
  cartNudgeContext.hydrated = true;
}

function cartContainsPilotJourney(cart) {
  return (cart?.items || []).some((item) =>
    item.handle === PILOT_HANDLE ||
    item.properties?._klover_bundle === 'free-shipping-set'
  );
}

function maybeEmitDiscountConfirmation(cart) {
  if (!confirmBundleDiscount(cart) || typeof sessionStorage === 'undefined') return;
  const key = `klover:discount-confirmed:${cart.token || 'unknown-cart'}`;
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, 'true');
  emitBundleEvent('bundle_discount_confirmed');
}

export function renderCartNudge(cart, host) {
  host?.querySelector?.('[data-klover-cart-nudge]')?.remove();
  if (!host || !cartContainsPilotJourney(cart)) return;

  const state = deriveCartNudgeState(cart, cartNudgeContext);
  if (state === 'none') return;

  const documentRef = host.ownerDocument || document;
  const nudge = documentRef.createElement('aside');
  nudge.className = 'klover-cart-nudge';
  nudge.dataset.kloverCartNudge = '';
  nudge.setAttribute('aria-live', 'polite');

  if (state === 'single_case') {
    const difference = Math.max(0, 6000 - Number(cart.total_price || 0));
    nudge.textContent = `You're ${formatMoney(difference, cartNudgeContext.currency)} away from free shipping. Build a Free-Shipping Set.`;
  } else if (state === 'missing_accessory') {
    const availableAccessories = cartNudgeContext.accessories
      .filter((product) => product.variants.some((variant) => variant.available));
    const lowest = lowestAvailablePrice(availableAccessories);
    const suggestions = rankInitialProducts(availableAccessories, ACCESSORY_PREFERRED_TERMS, 3);
    const message = documentRef.createElement('p');
    message.textContent = `Add one accessory from ${formatMoney(lowest || 0, cartNudgeContext.currency)}, get 50% off, and unlock free shipping.`;
    nudge.append(message);
    if (suggestions.length) {
      const list = documentRef.createElement('ul');
      for (const product of suggestions) {
        const item = documentRef.createElement('li');
        const link = documentRef.createElement('a');
        link.href = `/products/${encodeURIComponent(product.handle)}`;
        link.textContent = product.title;
        item.append(link);
        list.append(item);
      }
      nudge.append(list);
    }
  } else {
    nudge.textContent = 'Free shipping unlocked — your accessory is 50% off.';
    maybeEmitDiscountConfirmation(cart);
  }

  host.prepend(nudge);
}

async function refreshCartNudge(host) {
  try {
    const cart = await fetchCart();
    if (!cartContainsPilotJourney(cart)) {
      renderCartNudge(cart, host);
      return;
    }
    if (!cartNudgeContext.hydrated) await hydrateCartNudgeContext();
    renderCartNudge(cart, host);
  } catch {
    host?.querySelector?.('[data-klover-cart-nudge]')?.remove();
  }
}

function findProductForm(section) {
  const sectionRoot = section.closest?.('.shopify-section');
  const roots = [sectionRoot];
  if (typeof document !== 'undefined') roots.push(document);

  for (const root of roots) {
    if (!root) continue;
    const forms = Array.from(root.querySelectorAll?.('form[action*="/cart/add"]') || []);
    const form = forms.find((candidate) =>
      candidate.querySelector('[name="id"]') &&
      candidate.querySelector('button[name="add"], button[type="submit"]')
    );
    if (form) return form;

    const fallback = root.querySelector?.('form[action*="/cart/add"]');
    if (fallback?.querySelector('[name="id"]')) return fallback;
  }

  return null;
}

export function getPrimaryVariantId(section) {
  const value = findProductForm(section)?.querySelector('[name="id"]')?.value;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function lowestAvailablePrice(products) {
  const prices = products.flatMap((product) =>
    product.variants
      .filter((variant) => variant.available)
      .map((variant) => variant.priceCents)
  );
  return prices.length ? Math.min(...prices) : null;
}

function setBundleError(section, message = '') {
  const error = section.querySelector('[data-bundle-error]');
  if (!error) return;
  error.textContent = message;
  error.hidden = !message;
}

export function updateSummary(section, state) {
  const summary = section.querySelector('[data-bundle-summary]');
  const submit = section.querySelector('[data-bundle-submit]');
  const complete = isBundleEligible(state) &&
    Number.isInteger(state.firstVariant?.priceCents) &&
    Number.isInteger(state.secondVariant?.priceCents) &&
    Number.isInteger(state.accessoryVariant?.priceCents);

  if (submit) submit.disabled = !complete;
  if (!summary) return;
  if (!complete) {
    summary.innerHTML = '';
    return;
  }

  const casePrice = state.firstVariant.priceCents + state.secondVariant.priceCents;
  const totals = calculateBundleTotals(
    state.firstVariant.priceCents,
    state.secondVariant.priceCents,
    state.accessoryVariant.priceCents,
  );
  const currency = state.currency || 'USD';
  const shippingLine = totals.freeShippingUnlocked
    ? 'FREE SHIPPING UNLOCKED'
    : `Add ${formatMoney(6000 - totals.totalCents, currency)} more to reach free shipping`;

  summary.innerHTML = [
    `<p>Two phone cases: ${formatMoney(casePrice, currency)}</p>`,
    `<p>Accessory: ${formatMoney(state.accessoryVariant.priceCents, currency)}</p>`,
    `<p>Accessory discount: -${formatMoney(totals.discountCents, currency)}</p>`,
    `<p>Bundle total: ${formatMoney(totals.totalCents, currency)}</p>`,
    `<p>${shippingLine}</p>`,
  ].join('');
}

function optionValues(product, optionIndex) {
  return [...new Set(product.variants
    .map((variant) => variant.options[optionIndex])
    .filter((value) => typeof value === 'string' && value.length))];
}

function dispatchSelection(container, role, product, variant) {
  container.dispatchEvent(new CustomEvent(SELECTION_EVENT, {
    bubbles: true,
    detail: { role, product, variant },
  }));
}

function activateCard(container, wrapper, product, role) {
  for (const candidate of container.querySelectorAll('[data-bundle-product-id]')) {
    const active = candidate === wrapper;
    candidate.querySelector('.klover-bundle-card')?.setAttribute('aria-pressed', String(active));
    const options = candidate.querySelector('.klover-bundle-card__options');
    if (options) options.hidden = !active;
  }

  const selects = Array.from(wrapper.querySelectorAll('select'));
  const selectedOptions = selects.map((select) => select.value);
  const variant = findVariant(product, selectedOptions);
  const unavailable = wrapper.querySelector('[data-variant-unavailable]');
  if (unavailable) unavailable.hidden = Boolean(variant);
  dispatchSelection(container, role, product, variant);
}

export function renderProducts(container, products, role) {
  const documentRef = container.ownerDocument || document;
  const fragment = documentRef.createDocumentFragment();

  for (const product of products) {
    const wrapper = documentRef.createElement('div');
    wrapper.className = 'klover-bundle-product';
    wrapper.dataset.bundleProductId = String(product.id);

    const button = documentRef.createElement('button');
    button.type = 'button';
    button.className = 'klover-bundle-card';
    button.setAttribute('aria-pressed', 'false');

    const image = documentRef.createElement('img');
    image.alt = '';
    image.loading = 'lazy';
    if (product.image) image.src = product.image;

    const title = documentRef.createElement('span');
    title.className = 'klover-bundle-card__title';
    title.textContent = product.title;

    const price = documentRef.createElement('span');
    price.className = 'klover-bundle-card__price';
    const minimumPrice = lowestAvailablePrice([product]);
    price.textContent = minimumPrice === null ? 'Unavailable' : formatMoney(minimumPrice);

    button.append(image, title, price);

    const firstAvailable = product.variants.find((variant) => variant.available);

    let options = null;
    if (shouldRenderProductOptions(product)) {
      options = documentRef.createElement('div');
      options.className = 'klover-bundle-card__options';
      options.hidden = true;

      product.optionNames.forEach((optionName, optionIndex) => {
        const label = documentRef.createElement('label');
        label.textContent = optionName;
        const select = documentRef.createElement('select');
        select.setAttribute('aria-label', `${product.title} ${optionName}`);

        for (const value of optionValues(product, optionIndex)) {
          const option = documentRef.createElement('option');
          option.value = value;
          option.textContent = value;
          select.append(option);
        }
        if (firstAvailable?.options[optionIndex]) {
          select.value = firstAvailable.options[optionIndex];
        }
        select.addEventListener('change', () => activateCard(container, wrapper, product, role));
        label.append(select);
        options.append(label);
      });

      const unavailable = documentRef.createElement('p');
      unavailable.dataset.variantUnavailable = '';
      unavailable.textContent = 'This combination is unavailable';
      unavailable.hidden = true;
      options.append(unavailable);
    }

    button.addEventListener('click', () => activateCard(container, wrapper, product, role));
    wrapper.append(button);
    if (options) wrapper.append(options);
    fragment.append(wrapper);
  }

  container.replaceChildren(fragment);
}

function restoreRenderedSelection(container, productId, variant) {
  if (!productId || !variant) return;
  const wrappers = Array.from(container.querySelectorAll('[data-bundle-product-id]'));
  const selected = wrappers.find((wrapper) => Number(wrapper.dataset.bundleProductId) === productId);
  if (!selected) return;

  for (const wrapper of wrappers) {
    const active = wrapper === selected;
    wrapper.querySelector('.klover-bundle-card')?.setAttribute('aria-pressed', String(active));
    const options = wrapper.querySelector('.klover-bundle-card__options');
    if (options) options.hidden = !active;
  }
  Array.from(selected.querySelectorAll('select')).forEach((select, index) => {
    if (variant.options[index] !== undefined) select.value = variant.options[index];
  });
  const unavailable = selected.querySelector('[data-variant-unavailable]');
  if (unavailable) unavailable.hidden = true;
}

export function emitBundleEvent(name, detail = {}) {
  document.dispatchEvent(new CustomEvent(name, { detail }));
  if (window.Shopify?.analytics?.publish) {
    window.Shopify.analytics.publish(name, detail);
  }
}

function parseCurrentProduct(section) {
  try {
    const product = JSON.parse(section.dataset.currentProduct || '{}');
    return {
      ...product,
      variants: (product.variants || []).map((variant) => ({
        id: Number(variant.id),
        title: variant.title,
        options: variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean),
        available: Boolean(variant.available),
        priceCents: Number(variant.price),
      })),
    };
  } catch {
    return { variants: [] };
  }
}

function focusPrimarySelector(section, productForm) {
  const sectionRoot = section.closest?.('.shopify-section');
  const selector = sectionRoot?.querySelector(
    'variant-selects select, variant-radios input:not([type="hidden"]), .product-form__input select, .product-form__input input:not([type="hidden"])'
  );
  const fallback = productForm?.querySelector('[name="id"]:not([type="hidden"])');
  (selector || fallback)?.focus();
}

export async function initializeBundleBuilder(section) {
  if (section.dataset.bundleInitialized === 'true') return;
  section.dataset.bundleInitialized = 'true';

  const productForm = findProductForm(section);
  const currentProduct = parseCurrentProduct(section);
  const panel = section.querySelector('[data-bundle-panel]');
  const openButton = section.querySelector('[data-bundle-open]');
  const caseOnlyButton = section.querySelector('[data-case-only]');
  const submitButton = section.querySelector('[data-bundle-submit]');
  const caseContainer = section.querySelector('[data-second-case-list]');
  const accessoryContainer = section.querySelector('[data-accessory-list]');
  const state = {
    currency: window.Shopify?.currency?.active || 'USD',
    firstVariantId: null,
    firstVariant: null,
    secondVariantId: null,
    secondVariant: null,
    secondProductId: null,
    accessoryVariantId: null,
    accessoryVariant: null,
    accessoryProductId: null,
    allAccessoriesVisible: false,
    cases: [],
    accessories: [],
  };
  let requestInFlight = false;
  let retryButton = null;

  const clearRetryState = () => {
    retryButton?.remove();
    retryButton = null;
    submitButton?.removeAttribute('aria-busy');
  };

  const attemptBundleAdd = async () => {
    if (requestInFlight) return;
    if (!isBundleEligible(state)) {
      if (!state.firstVariantId) focusPrimarySelector(section, productForm);
      setBundleError(section, 'Select two phone-case variants and one accessory variant');
      return;
    }

    requestInFlight = true;
    clearRetryState();
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
    setBundleError(section);
    const selection = {
      firstVariantId: state.firstVariantId,
      secondVariantId: state.secondVariantId,
      accessoryVariantId: state.accessoryVariantId,
    };

    try {
      const cart = await addBundleToCart(selection);
      if (!verifyBundleCartLines(cart, selection)) {
        throw new Error('The set was added, but Shopify could not verify all three items');
      }
      emitBundleEvent('bundle_add_to_cart', {
        primaryProductId: Number(currentProduct.id) || null,
        secondProductId: state.secondProductId,
        accessoryProductId: state.accessoryProductId,
      });

      const code = section.dataset.testDiscountCode;
      if (code) {
        window.location.assign(`/discount/${encodeURIComponent(code)}?redirect=/cart`);
      } else {
        window.location.assign(`${cartRoot()}cart`);
      }
    } catch (error) {
      let cartHasSelection = false;
      try {
        const currentCart = await fetchCart();
        cartHasSelection = verifyBundleCartLines(currentCart, selection);
      } catch {
        // The original Shopify error remains the shopper-facing source of truth.
      }

      const message = error?.message || 'Unable to add the complete set';
      setBundleError(
        section,
        cartHasSelection ? `${message}. The selected set is already in your cart.` : message
      );
      emitBundleEvent('bundle_add_to_cart_error', {
        status: Number(error?.status) || null,
        primaryProductId: Number(currentProduct.id) || null,
        secondProductId: state.secondProductId,
        accessoryProductId: state.accessoryProductId,
      });

      retryButton = section.ownerDocument.createElement('button');
      retryButton.type = 'button';
      retryButton.className = 'klover-bundle__retry';
      retryButton.textContent = 'Retry adding set';
      retryButton.addEventListener('click', attemptBundleAdd, { once: true });
      submitButton.insertAdjacentElement('afterend', retryButton);
    } finally {
      requestInFlight = false;
      submitButton.removeAttribute('aria-busy');
    }
  };

  const updateOpenPrice = () => {
    const output = section.querySelector('[data-bundle-open-price]');
    const secondCasePrice = lowestAvailablePrice(state.cases);
    const accessoryPrice = lowestAvailablePrice(state.accessories);
    if (!output) return;
    if (!state.firstVariant || secondCasePrice === null || accessoryPrice === null) {
      output.textContent = '';
      return;
    }
    const totals = calculateBundleTotals(
      state.firstVariant.priceCents,
      secondCasePrice,
      accessoryPrice,
    );
    output.textContent = `From ${formatMoney(totals.totalCents, state.currency)}`;
  };

  const syncPrimaryVariant = () => {
    const id = getPrimaryVariantId(section);
    const variant = currentProduct.variants.find((candidate) => candidate.id === id && candidate.available) || null;
    state.firstVariantId = variant?.id || null;
    state.firstVariant = variant;

    const primaryPrice = section.querySelector('[data-primary-price]');
    const primaryVariant = section.querySelector('[data-primary-variant]');
    const caseOnlyPrice = section.querySelector('[data-case-only-price]');
    const price = variant ? formatMoney(variant.priceCents, state.currency) : '';
    if (primaryPrice) primaryPrice.textContent = price;
    if (caseOnlyPrice) caseOnlyPrice.textContent = price;
    if (primaryVariant) primaryVariant.textContent = variant?.title === 'Default Title' ? '' : (variant?.title || '');
    updateOpenPrice();
    updateSummary(section, state);
  };

  const renderCases = () => {
    renderProducts(caseContainer, state.cases, 'second-case');
    restoreRenderedSelection(caseContainer, state.secondProductId, state.secondVariant);
  };

  const renderAccessories = () => {
    const visible = state.allAccessoriesVisible
      ? state.accessories
      : rankInitialProducts(state.accessories, ACCESSORY_PREFERRED_TERMS, 3);
    renderProducts(accessoryContainer, visible, 'accessory');
    restoreRenderedSelection(accessoryContainer, state.accessoryProductId, state.accessoryVariant);
    const viewAll = section.querySelector('[data-view-all-accessories]');
    if (viewAll) viewAll.hidden = state.allAccessoriesVisible || state.accessories.length <= 3;
  };

  section.addEventListener(SELECTION_EVENT, (event) => {
    const { role, product, variant } = event.detail;
    if (role === 'second-case') {
      state.secondProductId = product.id;
      state.secondVariant = variant;
      state.secondVariantId = variant?.id || null;
      if (variant) {
        emitBundleEvent('bundle_second_case_selected', { productId: product.id, variantId: variant.id });
      }
    } else if (role === 'accessory') {
      state.accessoryProductId = product.id;
      state.accessoryVariant = variant;
      state.accessoryVariantId = variant?.id || null;
      if (variant) {
        emitBundleEvent('bundle_accessory_selected', { productId: product.id, variantId: variant.id });
      }
    }
    clearRetryState();
    setBundleError(section, variant ? '' : 'This combination is unavailable');
    updateSummary(section, state);
  });

  openButton?.addEventListener('click', () => {
    if (panel) panel.hidden = false;
    openButton.setAttribute('aria-expanded', 'true');
    emitBundleEvent('bundle_builder_opened');
  });

  caseOnlyButton?.addEventListener('click', () => {
    if (panel) panel.hidden = true;
    openButton?.setAttribute('aria-expanded', 'false');
    const buttons = productForm?.querySelector('.product-form__buttons') || productForm;
    buttons?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    productForm?.querySelector('button[name="add"], button[type="submit"]')?.focus();
  });

  productForm?.addEventListener('change', () => {
    setTimeout(syncPrimaryVariant, 0);
  });

  submitButton?.addEventListener('pointerdown', () => {
    if (!state.firstVariantId) focusPrimarySelector(section, productForm);
  });
  submitButton?.addEventListener('click', attemptBundleAdd);

  section.querySelector('[data-view-all-accessories]')?.addEventListener('click', () => {
    state.allAccessoriesVisible = true;
    renderAccessories();
  });

  syncPrimaryVariant();

  try {
    const catalog = await loadBundleCatalog({
      iphoneUrl: section.dataset.iphoneUrl,
      accessoriesUrl: section.dataset.accessoriesUrl,
    });
    state.cases = catalog.cases;
    state.accessories = catalog.accessories;
    cartNudgeContext.qualifyingCaseHandles = new Set([
      PILOT_HANDLE,
      ...state.cases.map((product) => product.handle),
    ]);
    cartNudgeContext.accessories = state.accessories;
    cartNudgeContext.currency = state.currency;
    cartNudgeContext.hydrated = true;

    const hasAvailableCase = state.cases.some((product) => product.variants.some((variant) => variant.available));
    const hasAvailableAccessory = state.accessories.some((product) => product.variants.some((variant) => variant.available));
    if (!hasAvailableCase || !hasAvailableAccessory) {
      section.hidden = true;
      return;
    }

    renderCases();
    renderAccessories();
    updateOpenPrice();
  } catch {
    section.hidden = true;
  }
}

function initializeBundleBuilders() {
  for (const section of document.querySelectorAll('[data-klover-bundle]')) {
    initializeBundleBuilder(section);
  }
}

function initializeCartNudges() {
  const drawerWrapper = document.querySelector('#halo-cart-sidebar .halo-sidebar-wrapper');
  if (drawerWrapper) {
    refreshCartNudge(drawerWrapper);
    const drawerContent = drawerWrapper.querySelector('.previewCart-wrapper');
    if (drawerContent && typeof MutationObserver !== 'undefined') {
      let refreshTimer;
      new MutationObserver(() => {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => refreshCartNudge(drawerWrapper), 100);
      }).observe(drawerContent, { childList: true, subtree: true });
    }
  }

  if (window.location?.pathname === '/cart') {
    const cartItems = document.querySelector('cart-items');
    const cartHost = cartItems?.parentElement || document.querySelector('main');
    if (cartHost) {
      refreshCartNudge(cartHost);
      if (cartItems && typeof MutationObserver !== 'undefined') {
        let refreshTimer;
        new MutationObserver(() => {
          clearTimeout(refreshTimer);
          refreshTimer = setTimeout(() => refreshCartNudge(cartHost), 100);
        }).observe(cartItems, { childList: true, subtree: true });
      }
    }
  }
}

function initializeKlover() {
  const hasBuilder = document.querySelector('[data-klover-bundle]');
  const hasCartHost =
    document.querySelector('#halo-cart-sidebar .halo-sidebar-wrapper') ||
    (window.location?.pathname === '/cart' && document.querySelector('cart-items, main'));
  if (!hasBuilder && !hasCartHost) return;
  initializeBundleBuilders();
  initializeCartNudges();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeKlover, { once: true });
  } else {
    initializeKlover();
  }
}

